import React from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowRightIcon, ImageIcon, BoltIcon, PencilIcon } from '../icons';
import { useCustomizer } from '../../store/customizer';
import { Bar, Btn, Label, Secondary, SecondaryLabel } from './WizardFooter.styles';

/**
 * Shared desktop footer bar (steps 1 to 3, incl. the connect-domain sub-flow).
 * The primary CTA is ALWAYS right-aligned and reads the canonical `mainNextLabel`
 * (identical to the mobile chrome); a secondary toggle sits on the LEFT:
 *  brand: "I have my own name & domain" / "I want to decide fast"
 *  logo:  "I have my own logo" / "I want to decide fast"
 * Step 4 (deploy) owns its whole layout and never mounts this.
 */
export const WizardFooter = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const {
    stepKey,
    goNext,
    canAdvance,
    connectStage,
    mainNextLabel,
    mainNextPlaceholders,
    aiLogo,
    setAiLogo,
    manualMode,
    setManualMode,
  } = useCustomizer();

  // Connect-domain input screen owns its CTA inside the card ("Link your domain"),
  // so the shared footer shows nothing there.
  if (connectStage === 'input') return null;
  // DNS-records screen: "Continue to Logo" unlocks only after entri succeeds
  // (canAdvance gates on entriSuccess).
  if (connectStage === 'dns') {
    return (
      <Bar>
        <Btn type="button" $enabled={canAdvance} disabled={!canAdvance} onClick={canAdvance ? goNext : undefined}>
          <Label text={mainNextLabel} placeholders={mainNextPlaceholders} tag="span" />
          <ArrowRightIcon size={20} />
        </Btn>
      </Bar>
    );
  }

  const enabled = canAdvance;

  let secondary: React.ReactNode = null;
  if (stepKey === 'brand') {
    secondary = (
      <Secondary type="button" onClick={() => setManualMode(!manualMode)}>
        {manualMode ? <BoltIcon size={18} /> : <PencilIcon size={20} />}
        <SecondaryLabel text={manualMode ? t('I want to decide fast') : t('I have my own name & domain')} tag="span" />
      </Secondary>
    );
  } else if (stepKey === 'logo') {
    secondary = (
      <Secondary type="button" onClick={() => setAiLogo(!aiLogo)}>
        {aiLogo ? <ImageIcon size={20} /> : <BoltIcon size={18} />}
        <SecondaryLabel text={aiLogo ? t('I have my own logo') : t('I want to decide fast')} tag="span" />
      </Secondary>
    );
  }

  return (
    <Bar>
      {secondary}
      <Btn type="button" $enabled={enabled} disabled={!enabled} onClick={enabled ? goNext : undefined}>
        <Label text={mainNextLabel} placeholders={mainNextPlaceholders} tag="span" />
        <ArrowRightIcon size={20} />
      </Btn>
    </Bar>
  );
};

export default WizardFooter;